import React, { useEffect, useState } from 'react';
import { useReadinessStore } from '../../../store/readinessStore';
import { SpinLoader } from '../../../components/loader/Loader';
import { toast } from 'react-toastify';
import Modal from '../../../components/modal/Modal';

interface ReadinessChecklistPerformProps {
  isOpen: boolean;
  onClose: () => void;
  resource: any;
  onSuccess?: () => void;
}

const ReadinessChecklistPerform: React.FC<ReadinessChecklistPerformProps> = ({ 
  isOpen, 
  onClose, 
  resource,
  onSuccess 
}) => {
  const { templates, fetchTemplates, performInspection, loading } = useReadinessStore();
  const [template, setTemplate] = useState<any>(null);
  const [responses, setResponses] = useState<any[]>([]);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (templates.length === 0) {
        fetchTemplates();
      }
    }
  }, [isOpen, templates.length, fetchTemplates]);

  useEffect(() => {
    if (isOpen && resource && templates.length > 0) {
      // Find template for this resource's emergency subcategory
      const foundTemplate = templates.find(t => 
        t.emergencySubcategoryId === resource.emergencySubcategoryId
      );
      
      if (foundTemplate) {
        setTemplate(foundTemplate);
        // Initialize responses
        setResponses(foundTemplate.checklistConfig.map((item: any) => ({
          controlId: item.controlId,
          prompt: item.prompt,
          type: item.type,
          value: item.type === 'BOOLEAN' ? null : (item.type === 'NUMERIC' ? 0 : ''),
          isCritical: item.isCritical
        })));
      } else {
        setTemplate(null);
        toast.error('No checklist template found for this resource type');
      }
    }
  }, [isOpen, resource, templates]);

  const handleResponseChange = (controlId: string, value: any) => {
    setResponses(prev => prev.map(r => 
      r.controlId === controlId ? { ...r, value } : r
    ));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate all items are filled
    const unfilled = responses.find(r => r.value === null || r.value === undefined);
    if (unfilled) {
      toast.error('Please complete all checklist items');
      return;
    }

    const inspectionData = {
      equipmentId: resource.id || resource._id,
      templateId: template.id || template._id,
      responses,
      notes,
      locationAtTimeOfCheck: resource.location || 'Site', // Usually captured from GPS or fixed location
    };

    const success = await performInspection(inspectionData);
    if (success) {
      if (onSuccess) onSuccess();
      onClose();
    }
  };


  return (
    <Modal isOpen={isOpen} onClose={onClose} modalClass="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] flex flex-col relative">
      <div className="flex justify-between items-center mb-4 border-b pb-2">
        <div>
          <h2 className="text-xl font-bold">Perform Readiness Inspection</h2>
          <p className="text-sm text-gray-500">Resource: {resource?.resourceId} - {resource?.location}</p>
        </div>
      </div>

      {!template ? (
        <div className="flex-1 flex flex-col items-center justify-center py-10">
          {loading ? <SpinLoader /> : (
            <div className="text-center">
              <p className="text-gray-500 mb-4">No template found for this resource category.</p>
              <button 
                onClick={onClose}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded"
              >
                Close
              </button>
            </div>
          )}
        </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto space-y-6 px-2 py-4">
              <div className="bg-blue-50 p-4 rounded border border-blue-100 mb-4">
                <h3 className="text-sm font-semibold text-blue-800 mb-1">{template.title}</h3>
                <p className="text-xs text-blue-600">Please answer all questions honestly. Items marked with "Critical" will affect the overall readiness status immediately.</p>
              </div>

              {responses.map((resp, index) => (
                <div key={resp.controlId} className="border-b pb-4 last:border-0">
                  <div className="flex justify-between mb-2">
                    <label className="text-sm font-medium text-gray-800 flex items-center gap-2">
                      {resp.prompt}
                      {resp.isCritical && <span className="bg-red-100 text-red-700 text-[10px] px-1.5 py-0.5 rounded font-bold uppercase tracking-wider">Critical</span>}
                    </label>
                  </div>

                  {resp.type === 'BOOLEAN' && (
                    <div className="flex gap-4">
                      <button
                        type="button"
                        onClick={() => handleResponseChange(resp.controlId, true)}
                        className={`flex-1 py-3 px-4 rounded border transition ${resp.value === true ? 'bg-green-100 border-green-500 text-green-800 font-bold' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                      >
                        Yes / Pass
                      </button>
                      <button
                        type="button"
                        onClick={() => handleResponseChange(resp.controlId, false)}
                        className={`flex-1 py-3 px-4 rounded border transition ${resp.value === false ? 'bg-red-100 border-red-500 text-red-800 font-bold' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                      >
                        No / Fail
                      </button>
                    </div>
                  )}

                  {resp.type === 'NUMERIC' && (
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        className="flex-1 border border-gray-300 rounded p-2 focus:ring-blue-500 outline-none"
                        value={resp.value}
                        onChange={(e) => handleResponseChange(resp.controlId, parseFloat(e.target.value))}
                      />
                      {/* We could add unit here from template if available */}
                    </div>
                  )}

                  {resp.type === 'PHOTO_PROOF' && (
                    <div className="border-2 border-dashed border-gray-200 rounded p-4 text-center cursor-pointer hover:bg-gray-50 transition">
                      {/* Simplified photo upload for demonstration */}
                      <p className="text-xs text-gray-500">Click to upload photo evidence</p>
                      <input 
                        type="file" 
                        accept="image/*" 
                        className="hidden" 
                        onChange={(e) => handleResponseChange(resp.controlId, "file_placeholder_" + e.target.files?.[0].name)}
                      />
                      {resp.value && <p className="text-[10px] text-green-600 mt-2">✓ Photo attached: {resp.value}</p>}
                    </div>
                  )}
                </div>
              ))}

              <div className="mt-6 border-t pt-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Observation Notes</label>
                <textarea
                  className="w-full border border-gray-300 rounded p-2 text-sm"
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Enter any specific observations or issues found..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-4 border-t pt-4">
              <button 
                type="button" 
                onClick={onClose}
                className="px-6 py-2 text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button 
                type="submit"
                className="flex items-center justify-center font-semibold gap-3 bg-[#0fa9a6] text-white px-4 py-2 rounded-full"
                disabled={loading}
              >
                {loading ? 'Submitting...' : 'Submit Inspection'}
              </button>
            </div>
          </form>
        )}
    </Modal>
  );
};

export default ReadinessChecklistPerform;
